import { useEffect, useMemo, useRef } from 'react';

import type { CFValidator } from '@shared-components/types/convertflow';

export type { CFValidator, CFValidatorReturn } from '@shared-components/types/convertflow';

export interface UseConvertFlowValidationOptions {
	/**
	 * Max ms to wait for `globalThis.window.convertflow.validations` to become a function
	 * (CF script still loading). Polling cadence is 100ms.
	 * @default 10_000
	 */
	readyTimeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 10_000;
const POLL_INTERVAL_MS = 100;

/**
 * Registers field-level validators with ConvertFlow's validation API so that
 * CF gates step advance until each field passes its consumer-supplied check.
 *
 * Behavior:
 * - For each `key` in `validators`, a stable wrapper is registered with CF.
 *   The wrapper reads the latest validator from a ref at call time, so passing
 *   inline arrow functions does NOT re-register on every render.
 * - Re-registration only happens when the SET of keys changes (sorted-joined
 *   signature). Pure function-identity changes are absorbed by the ref.
 * - If `globalThis.window.convertflow.validations` is not yet a function when the hook
 *   mounts, it polls every 100ms up to `readyTimeoutMs`.
 * - On unmount (or key-set change) the previously registered keys are deleted
 *   from `globalThis.window.convertflow.app.helpers.fieldValidations.registry`. Other
 *   registry entries are left intact.
 *
 * CF's validator contract (see `src/types/convertflow.d.ts`):
 *   `true | null | undefined` → field is valid
 *   `false`                  → invalid, CF shows its default message
 *   `string`                 → invalid, CF shows this string
 *   `Promise<above>`         → CF awaits
 *
 * If a validator THROWS or its promise REJECTS, CF logs the error and treats
 * the field as VALID (fail-open). Callers needing fail-closed semantics should
 * catch errors inside their validator and return a string.
 *
 * Multi-CTA behavior: CF's registry is global per page. Registering two
 * validators for the same bare field name is last-write-wins. Pages typically
 * host a single validating CF CTA, so this is acceptable for v1.
 *
 * SSR-safe: returns immediately when `window` is undefined.
 */
export function useConvertFlowValidation(
	validators: Record<string, CFValidator>,
	options: UseConvertFlowValidationOptions = {}
): void {
	const { readyTimeoutMs = DEFAULT_TIMEOUT_MS } = options;

	// Keep latest validators in a ref so wrappers always call the current fn
	// without re-registering on identity changes.
	const validatorsRef = useRef(validators);
	validatorsRef.current = validators;

	// Re-register only when the SET of keys changes.
	const keySignature = useMemo(
		() =>
			Object.keys(validators)
				.sort((a, b) => a.localeCompare(b))
				.join('|'),
		[validators]
	);

	useEffect(() => {
		if (globalThis.window === undefined) return;
		const keys = Object.keys(validatorsRef.current);
		if (keys.length === 0) return;

		const win = globalThis.window as Window & {
			convertflow?: {
				validations?: (v: Record<string, CFValidator>) => void;
				app?: { helpers?: { fieldValidations?: { registry: Record<string, CFValidator> } } };
				[k: string]: unknown;
			};
		};

		// Build the wrapper map. Each wrapper delegates to the ref-current validator.
		const wrappers: Record<string, CFValidator> = {};
		keys.forEach((key) => {
			wrappers[key] = (value, opts) => {
				const current = validatorsRef.current[key];
				if (!current) return true;
				return current(value, opts);
			};
		});

		let cancelled = false;
		let pollTimer: ReturnType<typeof setTimeout> | undefined;
		let registered = false;
		const startedAt = Date.now();

		const attemptRegister = (): void => {
			if (cancelled) return;

			const fn = win.convertflow?.validations;
			if (typeof fn === 'function') {
				fn(wrappers);
				registered = true;
				return;
			}

			if (Date.now() - startedAt >= readyTimeoutMs) return;
			pollTimer = setTimeout(attemptRegister, POLL_INTERVAL_MS);
		};

		attemptRegister();

		return () => {
			cancelled = true;
			if (pollTimer) clearTimeout(pollTimer);
			if (!registered) return;

			const registry = win.convertflow?.app?.helpers?.fieldValidations?.registry;
			if (!registry) return;

			keys.forEach((key) => {
				if (registry[key] === wrappers[key]) {
					delete registry[key];
				}
			});
		};
	}, [keySignature, readyTimeoutMs]);
}
